import React, { useCallback, useMemo, useState } from "react";

import { faPen } from "@fortawesome/pro-solid-svg-icons/faPen";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Popover from "antd/es/popover";
import Tooltip from "antd/es/tooltip";
import { useIntl } from "react-intl";

import AnyColor from "@mapmycustomers/shared/types/AnyColor";
import { Button } from "@mapmycustomers/ui";

import Dashboard from "@app/types/dashboard/Dashboard";
import useAnalytics from "@app/util/contexts/useAnalytics";
import useImagePalette from "@app/util/hook/useImagePalette";

import styles from "./Gradient.module.scss";
import PresetsMenu from "./PresetsMenu";

interface Props {
  auto?: boolean;
  colors?: AnyColor[];
  editable?: boolean;
  logoUrl?: string;
  onChange?: (
    settings: Partial<Pick<Dashboard["settings"], "gradient" | "gradientFromImage">>
  ) => void;
}

const Gradient: React.FC<Props> = ({ auto, colors, editable, logoUrl, onChange }) => {
  const analytics = useAnalytics();
  const intl = useIntl();
  const [popoverVisible, setPopoverVisibility] = useState(false);
  const [colorPickerVisible, setColorPickerVisibility] = useState(false);

  const [logoPalette, logoPaletteLoading] = useImagePalette(logoUrl);
  const innerColors = useMemo(() => {
    if (auto && logoPaletteLoading) {
      return ["transparent"]; // do not display anything until we know the final colors
    }

    return auto && logoPalette.length ? logoPalette : colors;
  }, [auto, logoPalette, logoPaletteLoading, colors]);

  const handleAutoChange = useCallback(
    (auto: boolean) => {
      analytics.clicked(["Change cover color"]);
      onChange?.({ gradient: undefined, gradientFromImage: auto });
    },
    [onChange, analytics]
  );

  const handleColorsChange = useCallback(
    (colors: AnyColor[]) => {
      setPopoverVisibility(false);
      analytics.clicked(["Change cover color"]);
      onChange?.({ gradient: colors, gradientFromImage: false });
    },
    [onChange, analytics]
  );

  const handleChangePopoverVisibility = useCallback(
    (visible: boolean) => {
      // only hide popover when a color picker is not visible
      setPopoverVisibility(colorPickerVisible || visible);
    },
    [colorPickerVisible]
  );

  return (
    <div
      className={styles.container}
      style={{
        backgroundColor: innerColors && innerColors.length === 1 ? innerColors[0] : undefined,
        backgroundImage:
          innerColors && innerColors.length > 1
            ? `linear-gradient(to right, ${innerColors?.join(", ")})`
            : undefined,
      }}
    >
      {editable && (
        <Popover
          content={
            <PresetsMenu
              auto={auto}
              colorPickerVisible={colorPickerVisible}
              colors={colors}
              onChangeAuto={handleAutoChange}
              onChangeColorPickerVisibility={setColorPickerVisibility}
              onChangeColors={handleColorsChange}
            />
          }
          onOpenChange={handleChangePopoverVisibility}
          open={popoverVisible}
          overlayClassName={styles.popover}
          trigger={["click"]}
        >
          <Tooltip
            placement="left"
            title={intl.formatMessage({
              id: "dashboard.header.gradient.edit",
              defaultMessage: "Change Cover Style",
              description: "Tooltip of the Edit Header's gradient button on the Dashboard page",
            })}
          >
            <Button
              className={styles.editButton}
              icon={<FontAwesomeIcon icon={faPen} />}
              iconOnly
              type="filled"
            />
          </Tooltip>
        </Popover>
      )}
    </div>
  );
};

export default Gradient;
